import { apiClient, apiClientPaginated } from '@/lib/api-client';
import type {
  Category,
  CategoryListResponse,
  CreateCategoryPayload,
  UpdateCategoryPayload,
} from '@/types/category';

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

// The API clamps `limit` to 100 (PaginationPipe).
const PAGE_SIZE = 100;

export const categoryService = {
  /**
   * Every category matching the filters. The pages that call this build a tree
   * or a picker from the result, so a missing row is a missing branch: it reads
   * every page up to `meta.totalPages` rather than trusting one page.
   */
  async list(params?: {
    search?: string;
    is_active?: boolean;
    parent_id?: string;
  }): Promise<CategoryListResponse> {
    const fetchPage = (page: number) =>
      apiClientPaginated<Category[]>(
        `/categories${buildQuery({ ...params, page, limit: PAGE_SIZE })}`,
      );
    const first = await fetchPage(1);
    const rest = await Promise.all(
      Array.from({ length: Math.max(first.meta.totalPages - 1, 0) }, (_, i) => fetchPage(i + 2)),
    );
    const data = [...first.data, ...rest.flatMap((r) => r.data)];
    return { data, meta: { ...first.meta, page: 1, limit: data.length, totalPages: 1 } };
  },

  getById(id: string): Promise<Category> {
    return apiClient<Category>(`/categories/${id}`);
  },

  create(payload: CreateCategoryPayload): Promise<Category> {
    return apiClient<Category>('/categories', {
      method: 'POST',
      body: payload,
    });
  },

  update(id: string, payload: UpdateCategoryPayload): Promise<Category> {
    return apiClient<Category>(`/categories/${id}`, {
      method: 'PATCH',
      body: payload,
    });
  },

  delete(id: string): Promise<void> {
    return apiClient<void>(`/categories/${id}`, { method: 'DELETE' });
  },
};
